/**
 * The clock and the date order of the operator, from the browser locale. Every rendered date and
 * time follows them (lib/time).
 */

import type { DisplayFormat } from '@/types/displayFormat';
import { DateOrder } from '@/types/displayFormat';

// Read only when Intl gives no answer for a locale, which real ICU never does for the clock.
const TWELVE_HOUR_REGIONS = ['US', 'CA', 'AU', 'NZ', 'IN', 'MX', 'KR', 'PH'];
const MONTH_FIRST_REGIONS = ['US'];
const YEAR_FIRST_REGIONS = ['JP', 'KR', 'CN', 'TW'];

/** The format of a region, for a locale whose Intl leaves a setting open. */
export function getRegionFormat(region: string): DisplayFormat {
    return {
        isHour24: !TWELVE_HOUR_REGIONS.includes(region),
        dateOrder: getRegionDateOrder(region),
    };
}

function getRegionDateOrder(region: string): DateOrder {
    if (MONTH_FIRST_REGIONS.includes(region)) {
        return DateOrder.MonthFirst;
    }
    return YEAR_FIRST_REGIONS.includes(region) ? DateOrder.YearFirst : DateOrder.DayFirst;
}

function getHour24(locale: string): boolean | undefined {
    const cycle = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hourCycle;
    return cycle === undefined ? undefined : ['h23', 'h24'].includes(cycle);
}

function getDateOrder(locale: string): DateOrder | undefined {
    const order = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    })
        .formatToParts(0)
        .map((part) => part.type)
        .filter((type) => ['year', 'month', 'day'].includes(type))
        .join(' ');
    switch (order) {
        case 'month day year':
            return DateOrder.MonthFirst;
        case 'day month year':
            return DateOrder.DayFirst;
        case 'year month day':
            return DateOrder.YearFirst;
        default:
            return undefined;
    }
}

/** The format that `locale` writes: Intl first, the locale's region where Intl is silent. */
export function getDisplayFormat(locale: string): DisplayFormat {
    const fallback = getRegionFormat(new Intl.Locale(locale).maximize().region ?? '');
    return {
        isHour24: getHour24(locale) ?? fallback.isHour24,
        dateOrder: getDateOrder(locale) ?? fallback.dateOrder,
    };
}

// Detected once: no setting changes it while the page is open. A per-user preference turns it
// into an atom.
export const DISPLAY_FORMAT: DisplayFormat = Object.freeze(
    getDisplayFormat(new Intl.DateTimeFormat().resolvedOptions().locale));
